import type { AxiosResponse } from 'axios'
import axios from './axios'
import type { BorrowerEmploymentData, BorrowerFinancialData, BorrowerFormData } from '@/types/borrower'
import type { LoanProperty } from '@/types/loan-property'
import type { LoanApplicationDeclarationData } from '@/types/borrower-declaration'
import type { LoanApplicationDemographicData } from '@/types/loan-demographics'
import type { LoanOriginatorData } from '@/types/loan-originator'
import type { MeridianEmploymentIncome, MeridianOtherIncome, MeridianDemographic, MeridianLoanOriginator, MeridianGiftOrGrants } from '@/types/meridianLoan/loan-details'
import type { Borrower } from '@/stores/pipeline/borrowerStore'
import type { MeridianAsset } from '@/types/meridianLoan/loan-details/meridianAssets'
import type { MeridianLiiabilityItem, MeridianRealPropertyRelatedLiabilities } from '@/types/meridianLoan/loan-details/meridianLiabilities'
import type { MeridianDemographics } from '@/types/meridianLoan/loan-details/meridianDemographics'

export interface LoanApplication {
  id: string
  loanNumber: string
  status: string
  brokerId: string
  companyId: string
  assignedTo?: string
  submittedAt?: Date
  borrower: {
    firstName: string
    lastName: string
    email: string
    phone: string
    dateOfBirth: Date
    ssn: string
    address: {
      street: string
      city: string
      state: string
      zip: string
    }
  }
  loanInfo: {
    loanType: string
    propertyType: string
    propertyValue: number
    loanAmount: number
    loanPurpose: string
    occupancyType: string
  }
  documents: Array<{
    id: string
    name: string
    type: string
    s3Key: string
    uploadedBy: string
    uploadedAt: Date
    status: string
  }>
  statusHistory: Array<{
    status: string
    updatedBy: string
    date: Date
    notes?: string
  }>
}

export interface CreateLoanData {
  borrower: {
    firstName: string
    lastName: string
    email: string
    phone: string
    dateOfBirth: Date
    ssn: string
    address: {
      street: string
      city: string
      state: string
      zip: string
    }
  }
  loanInfo: {
    loanType: string
    propertyType: string
    propertyValue: number
    loanAmount: number
    loanPurpose: string
    occupancyType: string
  }
}

interface APIFinancialPayload {
  assets: Array<{
    type: string;
    accountNumber: string;
    institutionName: string;
    balance: number;
    ownership: 'INDIVIDUAL' | 'JOINT';
  }>;
  otherAssets: Array<{
    type: string;
    description: string;
    value: number;
    ownership: 'INDIVIDUAL' | 'JOINT';
  }>;
  realEstate: Array<{
    propertyAddress: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
    propertyType: string;
    marketValue: number;
    mortgageBalance: number;
    ownership: 'INDIVIDUAL' | 'JOINT';
    rentalIncome: number;
    propertyStatus: string;
  }>;
  liabilities: Array<{
    type: string;
    accountNumber: string;
    creditorName: string;
    monthlyPayment: number;
    unpaidBalance: number;
    ownership: 'INDIVIDUAL' | 'JOINT';
  }>;
  otherLiabilities: Array<{
    type: string;
    description: string;
    monthlyPayment: number;
    unpaidBalance: number;
    ownership: 'INDIVIDUAL' | 'JOINT';
  }>;
  totalMonthlyIncome: number;
  totalMonthlyExpenses: number;
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
}

interface APILoanPropertyPayload {
  purpose: string;
  otherPurpose?: string;
  loanAmount: number;
  propertyAddress: {
    street: string;
    unit?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  propertyType: string;
  otherMortgages: Array<{
    creditorName: string;
    lienType: string;
    monthlyPayment: number;
    creditLimit: number;
    loanAmount: number;
  }>;
  rentalIncomes: Array<{
    rentalIncome: number;
    netIncome: number;
  }>;
  giftOrGrants: Array<{
    assetType: string;
    marketValue: number;
    source: string;
    isDeposited: boolean;
  }>;
}

interface APIDeclarationPayload {
  declarations: Array<{
    question: string;
    answer: string | null;
    explanation: string | null;
  }>;
}

export enum Ethnicity {
  HISPANIC_OR_LATINO = 'HISPANIC_OR_LATINO',
  NOT_HISPANIC_OR_LATINO = 'NOT_HISPANIC_OR_LATINO',
  DO_NOT_WISH_TO_PROVIDE = 'DO_NOT_WISH_TO_PROVIDE'
}

export enum Race {
  AMERICAN_INDIAN_OR_ALASKA_NATIVE = 'AMERICAN_INDIAN_OR_ALASKA_NATIVE',
  ASIAN = 'ASIAN',
  BLACK_OR_AFRICAN_AMERICAN = 'BLACK_OR_AFRICAN_AMERICAN',
  NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER = 'NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER',
  WHITE = 'WHITE',
  DO_NOT_WISH_TO_PROVIDE = 'DO_NOT_WISH_TO_PROVIDE'
}

export enum Sex {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  DO_NOT_WISH_TO_PROVIDE = 'DO_NOT_WISH_TO_PROVIDE'
}

interface APIDemographicPayload {
  ethnicity: {
    isHispanicOrLatino: boolean;
    hispanicOrigin: {
      mexican: boolean;
      puertoRican: boolean;
      cuban: boolean;
      other: string;
    };
    isNotHispanicOrLatino: boolean;
    doNotWishToProvide: boolean;
  };
  race: {
    isAmericanIndian: boolean;
    tribe: string;
    isAsian: boolean;
    asian: {
      isAsianIndian: boolean;
      isChinese: boolean;
      isFilipino: boolean;
      isJapanese: boolean;
      isKorean: boolean;
      isVietnamese: boolean;
      other: string;
    };
    isBlackOrAfricanAmerican: boolean;
    isPacificIslander: boolean;
    pacificIslander: {
      isNativeHawaiian: boolean;
      isGuamanianOrChamorro: boolean;
      isSamoan: boolean;
      other: string;
    };
    isWhite: boolean;
    doNotWishToProvide: boolean;
  };
  sex: 'female' | 'male' | 'doNotWishToProvide';
  collectionMethod: 'faceToFace' | 'telephone' | 'fax' | 'email';
  isEthnicityVisuallyObserved: boolean | null;
  isRaceVisuallyObserved: boolean | null;
}

export interface APILoanOriginatorPayload {
  name: string;
  nmlsId: string;
  stateLicenseId: string;
  phone: string;
  email: string;
  organization: {
    name: string;
    nmlsId: string;
    stateLicenseId: string;
    address: string;
  };
}

class LoanService {
  async getLoans(params?: {
    page?: number
    limit?: number
    status?: string
    loanType?: string
  }) {
    const response = await axios.get('/loans', { 
      params,
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    return response.data
  }

  async getLoan(id: string) {
    const response = await axios.get(`/loans/${id}`, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    return response.data
  }

  async createLoan(data: CreateLoanData) {
    const response = await axios.post('/loans', data, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    return response.data
  }

  async updateLoan(id: string, data: Partial<CreateLoanData>) {
    const response = await axios.put(`/loans/${id}`, data, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    return response.data
  }

  async deleteLoan(id: string) {
    const response = await axios.delete(`/loans/${id}`, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    return response.data
  }

  async updateLoanStatus(id: string, status: string, notes?: string) {
    const response = await axios.put(`/loans/${id}/status`, { status, notes }, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    return response.data
  }

  async assignLoan(id: string, userId: string | null) {
    const response = await axios.put(`/loans/${id}/assign`, { userId }, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    return response.data
  }

  async addLoanDocument(id: string, formData: FormData) {
    const response = await axios.post(`/loans/${id}/documents`, formData, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  async updateLoanDocument(loanId: string, documentId: string, data: { status: string; notes?: string }) {
    const response = await axios.put(`/loans/${loanId}/documents/${documentId}`, data, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    return response.data
  }

  async updateBorrowers(loanId: string, borrowers: Array<BorrowerFormData>) {
    const response = await axios.put(`/loans/${loanId}/borrowers`, {borrowers: borrowers})
    return response.data
  }

  async updateEmployment(loanId: string, data: BorrowerEmploymentData[]) {
    const response = await axios.put(`/loans/${loanId}/employment`, data)
    return response.data
  }

  async importMISMOLoan(formData: FormData) {
    const response = await axios.post('/loans/import-mismo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  async updateFinancial(loanId: string, data: BorrowerFinancialData[]) {
    const response = await axios.put(`/loans/${loanId}/financial`, data)
    return response.data
  }

  async updateLoanProperty(loanId: string, data: LoanProperty) {
    const response = await axios.put(`/loans/${loanId}/loan-property`, data)
    return response.data
  }

  async updateDeclaration(loanId: string, data: LoanApplicationDeclarationData[]) {
    const response = await axios.put(`/loans/${loanId}/declaration`, data)
    return response.data
  }

  async updateDemographic(loanId: string, data: LoanApplicationDemographicData[]) {
    return axios.put(`/loans/${loanId}/demographic`, data);
  }

  async updateLoanOriginator(loanId: string, data: LoanOriginatorData) {
    return axios.put(`/loans/${loanId}/loan-originator`, data);
  }

  async uploadDocument(loanId: string, formData: FormData) {
    const response = await axios.post(`/loans/${loanId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response
  }

  async getDocuments(loanId: string) {
    const response = await axios.get(`/loans/${loanId}/documents`)
    return response
  }

  async downloadDocument(loanId: string, documentId: string) {
    const response = await axios.get(`/loans/${loanId}/documents/${documentId}`, {
      withCredentials: true,
      responseType: 'blob',
      headers: {
        'Accept': 'application/json'
      }
    })
    return response
  }

  async deleteDocument(loanId: string, documentId: string): Promise<AxiosResponse> {
    return axios.delete(`/loans/${loanId}/documents/${documentId}`);
  }

  async getLoanBorrowerDetails(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/borrower-details`)
    return response.data
  }

  async getLoanFundedBorrowerDetails(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/funded-borrower-details`)
    return response.data
  }

  async getLoanDetailsQM(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/loan-details-qm`)
    return response.data
  }

  async getLoanConditions(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/details`)
    return response.data
  }

  async getLoanDocuments(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/documents`)
    return response.data
  }
  
  async getLoanNotes(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/notes`)
    return response.data
  }

  async getLoanTimelines(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/timelines`)
    return response.data
  }

  async getLoanData(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/data`)
    return response.data
  }

  async hideLoanSetupChecklist(loanNumber: string) {
    const response = await axios.post(`mortdash/${loanNumber}/hide-loan-setup-checklist`, {})
    return response.data
  }

  async getLoanFundedData(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/funded-data`)
    return response.data
  }

  async uploadLoan(data: any) {
    const response = await axios.post('/loans/upload', data)
    return response.data
  }

  async updateLoanCondition(data: {
    loan_number: string
    conditions: {
      id: string
      IsRequired: string
      Category: string
      CondDesc: string
      Notes: string
      DueDate: string
    }
    files: Array<{
      AssociatedDocId: string
      AssociatedDocType: string
    }>
  }) {
    const response = await axios.put(`mortdash/${data.loan_number}/update-condition`, data)
    return response.data
  }

  async getAgentContacts(loanNumber: string) {
    const response = await axios.get(`mortdash/${loanNumber}/agent-contacts`)
    return response.data
  }

  async updateAgentContacts(loanNumber: string, data: any) {
    const response = await axios.post(`mortdash/${loanNumber}/update-agent-contacts`, data)
    return response.data
  }

  async updateLoanPropertyData(data: any, loanNumber: string) {
    const response = await axios.put(`/loans/${loanNumber}/properties/data`, data)
    return response.data
  }

  async updateLoanAssetsData(data: MeridianAsset[], consumerAssetOwnerships: any[], loanNumber: string) {
    const response = await axios.put(`/loans/${loanNumber}/assets/data`, {
      assets: data,
      consumerAssetOwnerships: consumerAssetOwnerships
    })
    return response.data
  }

  async updateLoanLiabilitiesData(data: MeridianLiiabilityItem[], relation: MeridianRealPropertyRelatedLiabilities[], consumerLiabilities: any[], addedBalancePayment: any[], loanNumber: string, assetsRelation: any[]) {
    const response = await axios.put(`/loans/${loanNumber}/liabilities/data`, 
      {
        liabilities: data, 
        relations: relation, 
        consumerLiabilities: consumerLiabilities,
        addedBalancePayment: addedBalancePayment,
        assetsRelation: assetsRelation
      }
    )
    return response.data
  }

  async updateLoanIncomeSourcesData(data: {
   employmentData: MeridianEmploymentIncome[]
   otherSourceData: MeridianOtherIncome[]
  }, loanNumber: string) {
    const response = await axios.put(`/loans/${loanNumber}/income-sources/data`, data)
    return response.data
  }

  async updateLoanBorrowerData(data: Borrower[], loanNumber: string) {
    const response = await axios.post(`/loans/${loanNumber}/meridian-borrowers/data`, data)
    return response.data
  }

  async updateDeclarationData(data: Borrower[], loanNumber: string) {
    const response = await axios.post(`/loans/${loanNumber}/meridian-borrowers/data`, data)
    return response.data
  }
  
  async updateLoanDemographicsData(data: MeridianDemographics[], loanNumber: string) {
    const response = await axios.put(`/loans/${loanNumber}/meridian-demographics/data`, data)
    return response.data
  }

  async updateLoanPropertyInformation(data: any, loanNumber: string) {
    const response = await axios.put(`/loans/${loanNumber}/properties/information`, data, { showSnackbar: false })
    return response.data
  }

  async updateLoanGiftOrGrantsData(data: MeridianGiftOrGrants[], loanNumber: string) {
    const response = await axios.put(`/loans/${loanNumber}/assets/data`, data)
    return response.data
  }

  async updateLoanOriginatorInformationData(data: MeridianLoanOriginator, loanNumber: string) {
    const response = await axios.put(`/loans/${loanNumber}/loan-originator/information`, data)
    return response.data
  }

  async getULADBorrowerDetails(loanNumber: string) {
    const response = await axios.post(`mortdash/${loanNumber}/ulad-borrower/details`)
    return response.data
  }

  async getULAFundedBorrowerDetails(loanNumber: string) {
    const response = await axios.post(`mortdash/${loanNumber}/funded-ulad-borrower/details`)
    return response.data
  }

  async updateULADBorrowerDetails(data: any, loanNumber: string) {
    const response = await axios.post(`/loans/${loanNumber}/ulad-borrower/update`, data)
    return response.data
  }
  
  async getULADIncomeSourcesData(loanNumber: string) {
    const response = await axios.post(`/mortdash/${loanNumber}/ulad-income-sources/get`)
    return response.data
  }

  async updateULADIncomeSourcesData(data: any, loanNumber: string) {
    const response = await axios.post(`/loans/${loanNumber}/ulad-income-sources/update`, data)
    return response.data
  }

  async getLoanULADAssetsData(loanNumber: string) {
    const response = await axios.get(`/loans/${loanNumber}/ulad-assets`)
    return response.data
  }
  
  async getLoanULALiabilitiesData(loanNumber: string) {
    const response = await axios.get(`/loans/${loanNumber}/ulad-liabilities`)
    return response.data
  }

  async getUladLoanPropertiesData(data: any, loanNumber: string) {
    const response = await axios.post(`/loans/${loanNumber}/ulad-properties/get`, data)
    return response.data
  }

  async getLoanClosingDisclosureDetails(loanNumber: string) {
    const response = await axios.get(`/loans/${loanNumber}/ulad-closing-disclosure-details`)
    return response.data
  }

  async getLoanDetailsOfTransaction(loanNumber: string) {
    const response = await axios.get(`/loans/${loanNumber}/load-details-of-transaction`)
    return response.data
  }

  async updateDetailsOfTransaction(data: any, loanNumber: string) {
    const response = await axios.post(`/loans/${loanNumber}/details-of-transaction/update`, data)
    return response.data
  }

  async getLoanKeyDates(loanNumber: string) {
    const response = await axios.get(`/loans/${loanNumber}/key-dates`)
    return response.data
  }

  async getStatusCertificate(loanNumber: string, docTypeId: string) {
    const response = await axios.get(`/loans/${loanNumber}/status-certificate`, { params: { docTypeId } })
    return response.data
  }

  async getLoanDocOrderRequestDetails(loanNumber: string) {
    const response = await axios.get(`/loans/${loanNumber}/ulad-doc-order-details`)
    return response.data
  }

  async updateIsNonQM(loanNumber: string, data: any) {
    const response = await axios.post(`/loans/${loanNumber}/non-qm/update`, data)
    return response.data
  }

  async updateIsJumbo(loanNumber: string, data: { is_jumbo?: string; selectedProductType?: string }) {
    const response = await axios.post(`/loans/${loanNumber}/jumbo/update`, data)
    return response.data
  }

  async getLoanLockDetail(loanNumber: string) {
    const response = await axios.get(`/loans/${loanNumber}/lock-detail`)
    return response.data
  }

  async updateLoanDetails1003(loanNumber: string, data: any) {
    const loanId = typeof loanNumber === 'string' ? loanNumber : String((loanNumber as any)?.loanNumber ?? loanNumber)
    const response = await axios.post(`/loans/${loanId}/loan-details-1003/update`, data)
    return response.data
  }
  
  async getLoanClosingCostDetails(loanNumber: string) {
    const response = await axios.get(`/loans/${loanNumber}/closing-cost-details`)
    return response.data
  }

  /**
   * Save Anti-Steering Disclosure: hits BE (broker API) first; BE then calls Mortdash/Meridian Loan.Save.
   * Same path pattern as updateLoanDetails1003 – /loans/ is broker backend, not direct Mortdash.
   */
  async saveAntiSteeringDisclosure(loanNumber: string, data: { col1: Record<string, unknown>; col2: Record<string, unknown>; col3: Record<string, unknown> }) {
    const loanId = typeof loanNumber === 'string' ? loanNumber : String((loanNumber as any)?.loanNumber ?? loanNumber)
    const response = await axios.post(`/loans/${loanId}/anti-steering-disclosure/update`, data)
    return response.data
  }

  /** Mark anti-steering disclosure complete via document upload (sets has_requested_anti_steering_disclosure + date). */
  async completeAntiSteeringDisclosureViaUpload(loanNumber: string) {
    const loanId = typeof loanNumber === 'string' ? loanNumber : String((loanNumber as any)?.loanNumber ?? loanNumber)
    const response = await axios.post(`/loans/${loanId}/anti-steering-disclosure/complete-via-upload`, {})
  }
  
  async updatePortalMode(data: any) {
    const response = await axios.post(`/mortdash/pml/update-portal-mode`, data)
    return response.data
  }

  async getPortalMode() {
    const response = await axios.get(`/mortdash/pml/portal-mode`)
    return response.data
  }

  async loanVerification(loanNumber: string) {
    const response = await axios.get(`/mortdash/pml/${loanNumber}/loan-verification`)
    return response.data
  }
}

export default new LoanService() 